import React from "react";
import { Card } from "@/components/ui/card";

interface DepartmentCardProps {
  label: string;
  color: string;
  totalHires: number;
  currentRecruiters: number;
  monthlyGoal: number;
  timelineMonths: number;
  rampPeriodMonths: number;
}

export const DepartmentCard = ({ 
  label, 
  color, 
  totalHires, 
  currentRecruiters,
  monthlyGoal,
  timelineMonths,
  rampPeriodMonths,
}: DepartmentCardProps) => {
  // Calculate total delivery capability over the timeline for current team
  const totalTeamCapacity = currentRecruiters * monthlyGoal * timelineMonths;
  
  // Calculate shortfall
  const shortfall = Math.max(0, totalHires - totalTeamCapacity);

  // Calculate effective hiring months for new recruiters (excluding ramp period)
  const effectiveHiringMonths = Math.max(1, timelineMonths - rampPeriodMonths);
  
  // Calculate additional recruiters needed
  const additionalRecruitersNeeded = monthlyGoal > 0 
    ? Math.ceil(shortfall / (monthlyGoal * effectiveHiringMonths))
    : 0;

  // Check if we have growth hires but no monthly goal
  const hasError = totalHires > 0 && monthlyGoal === 0;

  console.log(`${label} Department Card Calculations:`, {
    totalHires,
    currentRecruiters,
    monthlyGoal,
    totalTeamCapacity,
    timelineMonths,
    effectiveHiringMonths,
    shortfall,
    additionalRecruitersNeeded,
    hasError,
    explanation: `Current team can hire ${totalTeamCapacity} (${currentRecruiters} recruiters × ${monthlyGoal} monthly goal × ${timelineMonths} months). Need ${shortfall} more hires. With ${effectiveHiringMonths} effective months (after ${rampPeriodMonths}-month ramp), need ${additionalRecruitersNeeded} new recruiters to achieve ${monthlyGoal} hires per month`
  });

  return (
    <Card className="p-4 border-l-4 h-full" style={{ borderLeftColor: color }}>
      <h3 className="font-semibold text-lg mb-3">{label}</h3>
      <div className="space-y-2">
        <div className="flex justify-between items-center min-h-[24px]">
          <span className="text-gray-600">Total Hires Needed:</span>
          <span className="font-medium text-green-500">
            {totalHires}
          </span>
        </div>
        <div className="flex justify-between items-center min-h-[24px]">
          <span className="text-gray-600 whitespace-nowrap">Current Team Capacity:</span>
          <span className="font-medium text-green-500">
            {totalTeamCapacity}
          </span>
        </div>
        <div className="flex justify-between items-center min-h-[24px]">
          <span className="text-gray-600">Hire Shortfall:</span>
          <span className={`font-medium ${shortfall === 0 ? 'text-green-500' : 'text-red-500'}`}>
            {shortfall}
          </span>
        </div>
        <div className="flex justify-between items-center min-h-[24px]">
          <span className="text-gray-600">Additional Recruiters:</span>
          <span className={`font-medium ${additionalRecruitersNeeded === 0 ? 'text-green-500' : 'text-red-500'}`}>
            {additionalRecruitersNeeded}
          </span>
        </div>
        {hasError && (
          <div className="text-sm text-red-500 font-medium">
            Missing recruiting hiring goals (hires/month)
          </div>
        )}
      </div>
    </Card>
  );
};